const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|mkv)$/i;
const AUDIO_EXT_RE = /\.(mp3|wav|m4a|aac|flac|ogg|opus|oga)$/i;
const MODEL_3D_EXT_RE = /\.(glb|gltf|obj|fbx|usdz|ply|stl)$/i;
const HTML_EXT_RE = /\.html?$/i;
const PDF_EXT_RE = /\.pdf$/i;
const PPTX_EXT_RE = /\.pptx$/i;
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MIME_BY_EXT = {
  '.html': 'text/html',
  '.htm': 'text/html',
  '.pdf': 'application/pdf',
  '.pptx': PPTX_MIME,
};
const NUMBERED_VARIANT_RE = /^(.*)-(\d{1,2})(\.[^.]+)$/i;
// episode2-shot-12.png is shot 12, not export revision 12 of episode2-shot.png.
const NUMBERED_SHOT_RE = /(?:^|[_-])(?:shot|clip|scene|keyframe|still|grab)[_-]?\d+\.[^.]+$/i;
const VERSIONED_VARIANT_RE = /^(.*)_v(\d{1,2})(\.[^.]+)$/i;
const REDO_VARIANT_RE = /^(.*?)[_-](redo|revised|retry|new|fixed|take\d+)(\.[^.]+)$/i;
// generate_video scratch: clip1.mp4, clip2-1.mp4, clip1_v.mp4, norm_clip3.mp4
// ffmpeg concat: normalized_0.mp4 … normalized_4.mp4
const SCRATCH_VIDEO_NAME_RE = /^(?:(?:norm(?:alized)?_)?clip\d+[a-z0-9_-]*|normalized_\d+)\.(mp4|mov|m4v|webm|mkv)$/i;
const SCRATCH_PATH_RE = /(?:^|\/)(?:tmp|scratch)\//i;
const BOOK_PAGE_IMAGE_RE = /^(p\d{1,3}|cover|.+_sheet)\.(png|jpe?g|webp|gif)$/i;
const DECK_SCRATCH_IMAGE_RE = /^(hero|product|slide[-_]?\d+|preview[-_]?\d+)\.(png|jpe?g|webp|gif)$/i;
const GATEWAY_UUID_NAME_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.[a-z0-9]+$/i;
const IMAGE_EXT = new Set(['.png', '.jpg', '.jpeg', '.webp', '.gif']);

export function isImageStillArtifact(artifact) {
  if (!artifact) return false;
  if (artifact.fileType === 'image') return true;
  if (String(artifact.mimeType || artifact.contentType || '').toLowerCase().startsWith('image/')) return true;
  const name = artifactBaseName(artifact);
  const dot = name.lastIndexOf('.');
  return dot > 0 && IMAGE_EXT.has(name.slice(dot).toLowerCase());
}

/** Stem used to match a re-fetched still (pete_master_v1-1.png → pete_master_v1.png). */
export function imageStillKey(nameOrPath) {
  const base = String(nameOrPath || '').split(/[/\\]/).pop().split('?')[0].toLowerCase();
  const match = !NUMBERED_SHOT_RE.test(base) && base.match(NUMBERED_VARIANT_RE);
  return match ? `${match[1]}${match[3]}` : base;
}

/** Map of still stem → sizes already shown on earlier turns. */
export function previousStillSizeIndex(artifacts) {
  const sizes = new Map();
  for (const artifact of artifacts || []) {
    if (!isImageStillArtifact(artifact)) continue;
    const key = imageStillKey(artifact.path || artifact.name);
    if (!key) continue;
    const size = Number(artifact.size) || 0;
    const set = sizes.get(key);
    if (set) set.add(size);
    else sizes.set(key, new Set([size]));
  }
  return sizes;
}

/**
 * True when this image is an unchanged re-export or fetch_url collision copy
 * of a still the thread already showed.
 */
export function isRedundantPreviousStill(artifact, previousIndex) {
  if (!previousIndex?.size || !isImageStillArtifact(artifact)) return false;
  const key = imageStillKey(artifact.path || artifact.name);
  const sizes = previousIndex.get(key);
  return !!(sizes && sizes.has(Number(artifact.size) || 0));
}

export function artifactBaseName(artifact) {
  const raw = artifact?.originalName
    || artifact?.name
    || artifact?.path
    || artifact?.cdnPath
    || artifact?.url
    || '';
  return String(raw).split(/[/\\]/).pop().split('?')[0] || '';
}

/**
 * fetch_url never overwrites: clip2.mp4 already taken becomes clip2-1.mp4.
 * Follow-up stitches also land as name_v2. Hide the superseded originals so
 * the thread does not re-show the old half next to the new one.
 */
export function collapseSupersededArtifacts(artifacts) {
  if (!Array.isArray(artifacts) || artifacts.length < 2) return artifacts || [];
  const names = new Set();
  for (const artifact of artifacts) {
    const name = artifactBaseName(artifact).toLowerCase();
    if (name) names.add(name);
  }
  const superseded = new Set();
  for (const name of names) {
    let match = !NUMBERED_SHOT_RE.test(name) && name.match(NUMBERED_VARIANT_RE);
    if (match) {
      const n = Number(match[2]);
      if (n >= 1) {
        superseded.add(`${match[1]}${match[3]}`.toLowerCase());
        for (let i = 1; i < n; i++) superseded.add(`${match[1]}-${i}${match[3]}`.toLowerCase());
      }
      continue;
    }
    match = name.match(VERSIONED_VARIANT_RE);
    if (match) {
      const n = Number(match[2]);
      if (n >= 1) {
        superseded.add(`${match[1]}${match[3]}`.toLowerCase());
        for (let i = 1; i < n; i++) superseded.add(`${match[1]}_v${i}${match[3]}`.toLowerCase());
      }
      continue;
    }
    match = name.match(REDO_VARIANT_RE);
    if (!match) continue;
    superseded.add(`${match[1]}${match[3]}`.toLowerCase());
  }
  return artifacts.filter((a) => !superseded.has(artifactBaseName(a).toLowerCase()));
}

export function isScratchVideoClipName(name) {
  const str = String(name || '');
  if (SCRATCH_PATH_RE.test(str)) return true;
  return SCRATCH_VIDEO_NAME_RE.test(str.split(/[/\\]/).pop() || '');
}

export function isBookPageImageName(name) {
  return BOOK_PAGE_IMAGE_RE.test(String(name || '').split(/[/\\]/).pop() || '');
}

export function isGatewayUuidFileName(name) {
  return GATEWAY_UUID_NAME_RE.test(String(name || '').split(/[/\\]/).pop() || '');
}

export function isDeckScratchImageName(name) {
  return DECK_SCRATCH_IMAGE_RE.test(String(name || '').split(/[/\\]/).pop() || '');
}

function fileExt(name) {
  const base = String(name || '').split(/[/\\]/).pop() || '';
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot).toLowerCase() : '';
}

/**
 * Raw generate_video clips, picture-book page sheets, and deck kit images
 * (hero.png fetched for a slide) belong in tmp/. When a named film, book
 * PDF, or deck is also present, hide the scratch files so Outputs and
 * inline cards show the deliverable the user asked for.
 */
export function collapseScratchIntermediates(artifacts) {
  if (!Array.isArray(artifacts) || artifacts.length < 2) return artifacts || [];
  const hasNamedVideo = artifacts.some((a) => (
    isVideoArtifact(a) && !isScratchVideoClipName(artifactBaseName(a))
  ));
  const hasBookPdf = artifacts.some((a) => isPdfArtifact(a));
  const hasPptx = artifacts.some((a) => isPptxArtifact(a));
  const namedExts = new Set();
  for (const a of artifacts) {
    const name = artifactBaseName(a);
    if (!isGatewayUuidFileName(name)) {
      const ext = fileExt(name);
      if (ext) namedExts.add(ext);
    }
  }
  const hasNamedUuidTwin = namedExts.size > 0 && artifacts.some((a) => isGatewayUuidFileName(artifactBaseName(a)));
  if (!hasNamedVideo && !hasBookPdf && !hasPptx && !hasNamedUuidTwin) return artifacts;
  return artifacts.filter((a) => {
    const name = artifactBaseName(a);
    if (hasNamedVideo && isScratchVideoClipName(name)) return false;
    if (hasBookPdf && isBookPageImageName(name)) return false;
    if ((hasPptx || hasBookPdf) && isDeckScratchImageName(name)) return false;
    if ((hasPptx || hasBookPdf) && isGatewayUuidFileName(name) && IMAGE_EXT.has(fileExt(name))) return false;
    if (isGatewayUuidFileName(name) && namedExts.has(fileExt(name))) return false;
    return true;
  });
}

export function collapseDuplicateBasenames(artifacts) {
  if (!Array.isArray(artifacts) || artifacts.length < 2) return artifacts || [];
  const best = new Map();
  for (const artifact of artifacts) {
    const name = artifactBaseName(artifact).toLowerCase();
    if (!name) continue;
    const prev = best.get(name);
    if (!prev) {
      best.set(name, artifact);
      continue;
    }
    const score = (item) => (
      (Number(item?.size) || 0)
      + ((item?.cdnPath || item?.url) ? 10 : 0)
    );
    if (score(artifact) >= score(prev)) best.set(name, artifact);
  }
  const seen = new Set();
  const out = [];
  for (const artifact of artifacts) {
    const name = artifactBaseName(artifact).toLowerCase();
    if (!name) {
      out.push(artifact);
      continue;
    }
    if (seen.has(name)) continue;
    seen.add(name);
    out.push(best.get(name));
  }
  return out;
}

export function artifactsNotCoveredByMessage(artifacts, { files = [], images = [] } = {}) {
  const names = new Set();
  for (const file of files || []) {
    const name = artifactBaseName(file).toLowerCase();
    if (name) names.add(name);
  }
  for (const image of images || []) {
    if (!image || typeof image === 'string') continue;
    const name = artifactBaseName(image).toLowerCase();
    if (name) names.add(name);
  }
  if (!names.size) return artifacts || [];
  return (artifacts || []).filter((artifact) => {
    const name = artifactBaseName(artifact).toLowerCase();
    return !name || !names.has(name);
  });
}

export function presentWorkArtifacts(artifacts) {
  return collapseDuplicateBasenames(
    collapseScratchIntermediates(collapseSupersededArtifacts((artifacts || []).filter(artifact => !artifact.isSuperseded))),
  );
}

export function isVideoArtifact(artifact) {
  if (!artifact) return false;
  if (artifact.fileType === 'video') return true;
  if (String(artifact.mimeType || '').toLowerCase().startsWith('video/')) return true;
  return VIDEO_EXT_RE.test(artifact.originalName || artifact.name || artifact.path || '');
}

export function isAudioArtifact(artifact) {
  if (!artifact) return false;
  if (artifact.fileType === 'audio') return true;
  if (String(artifact.mimeType || '').toLowerCase().startsWith('audio/')) return true;
  return AUDIO_EXT_RE.test(artifact.originalName || artifact.name || artifact.path || '');
}

export function isModel3DArtifact(artifact) {
  if (!artifact) return false;
  if (artifact.fileType === '3d') return true;
  if (String(artifact.mimeType || '').toLowerCase().startsWith('model/')) return true;
  return MODEL_3D_EXT_RE.test(artifact.name || artifact.path || '');
}

export function model3DFormatFromArtifact(artifact) {
  const haystack = String(artifact?.name || artifact?.path || '');
  const match = haystack.match(MODEL_3D_EXT_RE);
  return match ? match[1].toLowerCase() : 'glb';
}

export function isHtmlArtifact(artifact) {
  if (!artifact) return false;
  if (String(artifact.mimeType || '').toLowerCase() === 'text/html') return true;
  return HTML_EXT_RE.test(artifact.originalName || artifact.name || artifact.path || '');
}

/** Filename → title shown on the chat play card (`snake-game.html` → `Snake Game`). */
export function playableHtmlTitle(artifactOrName) {
  const raw = typeof artifactOrName === 'string'
    ? artifactOrName
    : artifactBaseName(artifactOrName);
  const base = raw.replace(/\.html?$/i, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!base) return 'Game';
  return base.replace(/\b[a-z]/g, (char) => char.toUpperCase());
}

export function isPdfArtifact(artifact) {
  if (!artifact) return false;
  if (String(artifact.mimeType || '').toLowerCase() === 'application/pdf') return true;
  return PDF_EXT_RE.test(artifact.originalName || artifact.name || artifact.path || '');
}

export function isPptxArtifact(artifact) {
  if (!artifact) return false;
  if (String(artifact.mimeType || '').toLowerCase() === PPTX_MIME) return true;
  return PPTX_EXT_RE.test(artifact.name || artifact.path || '');
}

/** File-like object so chat PDF / PPTX preview / openMessageFile can take a Work artifact. */
export function coworkerArtifactAsFile(artifact, url) {
  const name = String(artifact?.name || artifact?.path || 'file').split('/').pop() || 'file';
  const ext = name.includes('.') ? `.${name.split('.').pop()}` : '';
  const mimeType = artifact?.mimeType
    || MIME_BY_EXT[ext.toLowerCase()]
    || 'application/octet-stream';
  return {
    name,
    mimeType,
    cdnPath: url,
    url,
    extension: ext,
    fileType: artifact?.fileType,
    // Studio/provenance signals — see video_project.js conversationMediaStudio.
    ...(artifact?.origin ? { origin: String(artifact.origin) } : {}),
    ...(artifact?.studio === true ? { studio: true } : {}),
    ...(artifact?.fileId || artifact?.file?.id
      ? { id: String(artifact.fileId || artifact.file.id) }
      : {}),
  };
}
